import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Checkbox,
  Dialog,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
  Field,
  FieldError,
  FieldLabel,
  Icon,
  Label,
  Select,
  SelectItem,
  SelectPopup,
  SelectTrigger,
  SelectValue,
  Text,
  useIsMobile,
  type Omittable,
} from "@salestrak/ui";
import { DateText } from "#app/components/date-text";
import { RichTextEditor } from "#app/components/editor/rich-text-editor";
import { ProjectOutcomePicker } from "#app/components/updates/project-outcome-picker";
import {
  EDITOR_CONTROL_SELECTOR,
  PICKER_CONTROL_SELECTOR,
  focusFirstInvalid,
} from "#app/lib/utils/focus-first-invalid";
import { isRichTextEmpty } from "#app/lib/utils/rich-text-value";
import { toastMutationError } from "#app/lib/utils/toast-errors";
import { useUpdateAgeDialog } from "#app/lib/hooks/use-update-age-dialog";
import { formatRelativeDate } from "#app/lib/utils/format-relative-date";
import { entityFromAssigneeRef, resolveEntityLabel } from "#app/lib/entity-visuals";
import { AGE_STATUS } from "#app/lib/age-status";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { effectiveProjectCycle, liveAgeStatus, type ProjectAge } from "#app/lib/project-updates";
import { AnalyticsContext, IssueEvent, useAnalytics } from "#app/lib/analytics";
import { PROPERTY_ICON } from "#app/lib/constants/property-icons";

export type UpdateAgeDialogProps = Omittable<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  issueId: string;
  stageId?: string | null;
  age?: ProjectAge | null;
  /** The surface that opened the dialog, for `issue.update_age` analytics:
   *  "card", "detail_header", "list_row", "widget_table", "widget_list",
   *  "widget_data", "follow_up_widget", "added_list". */
  via: string;
}>;

/**
 * Posting one Update on a Project. The act is a single verb — a poster says
 * what happened and, optionally, picks the Outcome word for it; the status is
 * not a question, because posting IS what puts a Project back on track. The
 * cycle facts (last, next, which rung set it) live on the Summary's Follow ups
 * card, so this dialog leads with its first question instead of restating them;
 * the one fact it keeps, when the clock last reset, is the footer's leading
 * meta — the slot Start Follow up's "First update" date sits in — so the
 * header is the title alone and the sheet's title row stays one rung.
 */
export function UpdateAgeDialog({
  open,
  onOpenChange,
  issueId,
  stageId,
  age,
  via,
}: UpdateAgeDialogProps) {
  const { track } = useAnalytics();
  const { stageMap, workflowMap } = useWorkspaceData();
  const stage = stageId ? stageMap.get(stageId) : undefined;
  const workflow = stage?.workflowId
    ? workflowMap.get(stage.workflowId)
    : undefined;
  const effective = effectiveProjectCycle(age, stage, workflow);
  // Posting is available whenever a cycle runs; no clock is the one
  // closed door, and the Start Follow up dialog is what opens it.
  const postingDisabled = !!stageId && effective.cycle == null;

  const { settings, reasons, defaultReason, updateAge, canAssignSelf, otherAssignees } =
    useUpdateAgeDialog(open && !postingDisabled, issueId);
  const isMobile = useIsMobile();
  const lastUpdate = age?.date ? formatRelativeDate(age.date) : null;

  const [selectedReasonId, setSelectedReasonId] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const [assignMe, setAssignMe] = useState(false);
  // What "Assign to me" does to the people already on the Project. Keep is
  // the default — it is what the box has always done, and it destroys
  // nothing; Replace is the one press deeper.
  const [assignMode, setAssignMode] = useState<"keep" | "replace">("keep");
  const [submitting, setSubmitting] = useState(false);
  // Errors show only after a post was tried without a required answer; until
  // then the "Required" word on the label is the whole ask.
  const [attempted, setAttempted] = useState(false);
  const editorKeyRef = useRef(0);
  const outcomeFieldRef = useRef<HTMLDivElement>(null);
  const noteFieldRef = useRef<HTMLDivElement>(null);
  // Focus lands on the popup, not the first field: a ring already lit on the
  // Outcome control at open read as a stray keyboard state.
  const popupRef = useRef<HTMLDivElement>(null);

  // Reset state only on open transition (not when data refreshes mid-edit)
  const prevOpenRef = useRef(false);
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      setSelectedReasonId(defaultReason?.reasonId ?? null);
      setNote("");
      setAssignMe(false);
      setAssignMode("keep");
      setSubmitting(false);
      setAttempted(false);
      editorKeyRef.current += 1;
    }
    prevOpenRef.current = open;
  }, [open, defaultReason?.reasonId]);

  // The people the viewer would join or replace: one name, or a count.
  const othersLabel = useMemo(() => {
    const [first, ...rest] = otherAssignees;
    if (!first) return null;
    if (rest.length === 0) return resolveEntityLabel(entityFromAssigneeRef(first));
    return `${otherAssignees.length} others`;
  }, [otherAssignees]);

  // Post update is never disabled: a dead button cannot say what is missing,
  // and the posting rules are workspace settings the poster has no way to
  // know up front. Pressing it with a required answer empty marks that Field,
  // writes one line under it, and lands focus there; the mark clears as the
  // answer arrives.
  const outcomeMissing = settings.requireReason && !selectedReasonId;
  const commentMissing = settings.requireComment && isRichTextEmpty(note);
  const outcomeError = attempted && outcomeMissing;
  const commentError = attempted && commentMissing;

  const handleSubmit = useCallback(async () => {
    if (submitting) return;
    if (outcomeMissing || commentMissing) {
      setAttempted(true);
      focusFirstInvalid([
        {
          root: outcomeFieldRef.current,
          selector: PICKER_CONTROL_SELECTOR,
          invalid: outcomeMissing,
        },
        {
          root: noteFieldRef.current,
          selector: EDITOR_CONTROL_SELECTOR,
          invalid: commentMissing,
        },
      ]);
      return;
    }
    setSubmitting(true);
    try {
      await updateAge({
        issueId,
        status: AGE_STATUS.onTrack,
        note,
        reasonId: selectedReasonId,
        ...(assignMe && canAssignSelf ? { assignSelf: assignMode } : {}),
      });
    } catch (err) {
      toastMutationError(err, "Failed to post update");
      setSubmitting(false);
      return;
    }
    track({
      context: AnalyticsContext.Issue,
      event: IssueEvent.UpdateAge,
      data: {
        via,
        hasNote: !isRichTextEmpty(note),
        hasOutcome: selectedReasonId !== null,
        assignSelf: assignMe && canAssignSelf ? assignMode : "none",
        // Where the clock stood when the rep posted: before or after it went
        // stale. Null when no cycle measures the Project.
        statusBefore: liveAgeStatus(age, stage, workflow) ?? "none",
      },
    });
    setSubmitting(false);
    onOpenChange(false);
  }, [
    outcomeMissing,
    commentMissing,
    submitting,
    updateAge,
    issueId,
    note,
    selectedReasonId,
    assignMe,
    assignMode,
    canAssignSelf,
    onOpenChange,
    track,
    via,
    age,
    stage,
    workflow,
  ]);

  // No host offers an Update press while the clock is off — every affordance
  // gates on `clockRunning` — so this is only the seam against a stale row
  // mid-refetch: nothing to post onto, nothing to show.
  if (postingDisabled) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPopup
        size="lg"
        data-testid="update-age-dialog"
        initialFocus={popupRef}
        ref={popupRef}
        dialogOptions={{
          // The title carries the full phrase; the button is the bare verb,
          // the way Create Team's is "Create" — on the phone sheet the two
          // sit on one line, and "Post update · Post update" read twice.
          primary: { label: "Post", onClick: handleSubmit, loading: submitting },
          cancel: { disabled: submitting },
          // Leading meta, the way Start Follow up leads with its first due
          // date: "Last update" muted, the age in the value weight, the full
          // timestamp on hover like every abridged date. The Assign-to-me
          // controls follow it on the same rung and wrap under it on a phone.
          start: lastUpdate || canAssignSelf ? (
            <Box direction="row" align="center" gap="md" minWidth={0} wrap>
              {lastUpdate ? (
                <Box direction="row" align="center" gap="xs" minWidth={0}>
                  <Text size="sm" variant="muted">
                    Last update
                  </Text>
                  <DateText
                    date={age?.date}
                    label={lastUpdate}
                    size="sm"
                    weight="medium"
                    truncate={false}
                  />
                </Box>
              ) : null}
              {canAssignSelf ? (
                <Box direction="row" align="center" gap="xs" minWidth={0}>
                  <Label>
                    <Checkbox
                      checked={assignMe}
                      onCheckedChange={(checked) => setAssignMe(checked)}
                    />
                    <Text size="sm" weight="normal">
                      Assign to me
                    </Text>
                  </Label>
                  {/* Only once the box is checked AND someone else is on the
                      Project does a second word appear: a ghost select reading
                      "with Robert", whose two rows are Keep (default, what the box
                      always did) and Replace. Nobody else assigned, or the box
                      unchecked, and the footer is the one checkbox. */}
                  {assignMe && othersLabel ? (
                    <Select
                      value={assignMode}
                      onValueChange={(value: string | null) => {
                        if (value === "keep" || value === "replace") setAssignMode(value);
                      }}
                    >
                      <SelectTrigger
                        aria-label="Other assignees"
                        size="sm"
                        variant="ghost"
                        width="auto"
                        chevronIcon="chevron-down"
                      >
                        <SelectValue>
                          <Text size="sm" variant="muted">
                            {assignMode === "keep" ? `with ${othersLabel}` : `instead of ${othersLabel}`}
                          </Text>
                        </SelectValue>
                      </SelectTrigger>
                      <SelectPopup>
                        <SelectItem value="keep">{`Keep ${othersLabel}`}</SelectItem>
                        <SelectItem value="replace">{`Replace ${othersLabel}`}</SelectItem>
                      </SelectPopup>
                    </Select>
                  ) : null}
                </Box>
              ) : null}
            </Box>
          ) : null,
        }}
      >
        <DialogHeader>
          {/* On the phone sheet the title row is × · title · Post, and a
              glyph beside a centred title crowds the one rung: the title
              stands alone there. The dialog keeps the follow-up mark. */}
          {isMobile ? (
            <DialogTitle>Post update</DialogTitle>
          ) : (
            <Box direction="row" align="center" gap="sm">
              <Icon name={PROPERTY_ICON.followUp} size="lg" color="muted" />
              <DialogTitle>Post update</DialogTitle>
            </Box>
          )}
        </DialogHeader>
        <DialogPanel>
          <Box direction="column" gap="md">
            {/* Always asked — the outcome vocabulary has no off switch.
                "Require outcome" decides only whether the empty answer exists
                and whether posting waits for one. */}
            <Field invalid={outcomeError} ref={outcomeFieldRef}>
              <FieldLabel required={settings.requireReason}>Outcome</FieldLabel>
              <ProjectOutcomePicker
                reasons={reasons}
                value={selectedReasonId}
                onValueChange={setSelectedReasonId}
                required={settings.requireReason}
              />
              {outcomeError ? (
                <FieldError match>Pick an outcome to post this update.</FieldError>
              ) : null}
            </Field>

            <Field invalid={commentError} ref={noteFieldRef}>
              <FieldLabel required={settings.requireComment}>
                Update details
              </FieldLabel>
              {/* The editor is not a Field control, so its shell wears the
                  invalid rung itself. */}
              <Box
                border
                borderColor={commentError ? "invalid" : undefined}
                rounded="lg"
                overflow="hidden"
                fill="width"
              >
                <RichTextEditor
                  label="Update details"
                  key={editorKeyRef.current}
                  content=""
                  onUpdate={setNote}
                  placeholder="What happened?"
                  shortcutHints
                  mentions
                  compact
                  contentPaddingX="sm"
                  contentPaddingY="sm"
                />
              </Box>
              {commentError ? (
                <FieldError match>Say what happened.</FieldError>
              ) : null}
            </Field>
          </Box>
        </DialogPanel>
      </DialogPopup>
    </Dialog>
  );
}
